import { AlertTriangle, Clock, KeyRound, Shield, Skull } from 'lucide-react';

import {
  AuthExpired,
  CloudflareChallenge,
  NetworkError,
  ParseError,
  QuotaExceeded,
  RateLimited,
  SadPanda,
  SourceError,
} from '../../../lib/errors.ts';

export interface MappedSourceError {
  title: string;
  body: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Visual style — destructive (red) or warning (amber). */
  variant: 'destructive' | 'warning';
  /** Optional in-app CTA link. */
  ctaTo?: string;
  ctaLabel?: string;
}

/**
 * Convert any thrown value into a user-friendly banner spec. Falls back to a
 * generic "something went wrong" for unknown errors so we never crash the UI.
 *
 * Pure function — no React, no IndexedDB. Tested by SourceErrorBanner and
 * potentially callers outside the reader.
 */
export function describeSourceError(
  error: unknown,
  sourceId?: string,
  opts: { formLogin?: boolean } = {},
): MappedSourceError {
  if (error instanceof CloudflareChallenge) {
    return {
      title: 'Cloudflare verification needed',
      body: 'This source returned a Cloudflare browser challenge. Retry the request first. If the challenge persists, open the site in your normal browser, solve it, then refresh your connection cookies.',
      icon: Shield,
      variant: 'warning',
      ctaTo: sourceId ? `/connect/${sourceId}` : '/settings',
      ctaLabel: 'Refresh cookies',
    };
  }
  if (error instanceof SadPanda) {
    return {
      title: 'Sad Panda',
      body: 'ExHentai rejected the `igneous` access cookie. Log into the e-hentai forums, open exhentai.org in the same browser, confirm it shows galleries, then re-paste ipb_member_id, ipb_pass_hash, and igneous from that browser profile.',
      icon: Skull,
      variant: 'destructive',
      ctaTo: sourceId ? `/connect/${sourceId}` : '/connect/exhentai',
      ctaLabel: 'Re-paste cookies',
    };
  }
  if (error instanceof AuthExpired && opts.formLogin) {
    return {
      title: 'Login expired',
      body: 'Your login for this source has expired. Log in again to continue — tick "Remember login on this device" to have Bunko renew it automatically next time.',
      icon: KeyRound,
      variant: 'warning',
      ctaTo: sourceId ? `/connect/${sourceId}` : '/settings',
      ctaLabel: 'Log in again',
    };
  }
  if (error instanceof AuthExpired) {
    return {
      title: 'Login expired',
      body: 'Your session for this source has expired. Re-paste fresh cookies from your browser to continue.',
      icon: KeyRound,
      variant: 'warning',
      ctaTo: sourceId ? `/connect/${sourceId}` : '/settings',
      ctaLabel: 'Reconnect account',
    };
  }
  // RateLimited subclasses QuotaExceeded, so transport-specific guidance must come first.
  if (error instanceof RateLimited) {
    const when = error.retryAfterText ? ` It should lift in about ${error.retryAfterText}.` : '';
    if (error.egress === 'client') {
      return {
        title: 'Source temporarily rate-limited your connection',
        body: `The source temporarily blocked your network address for sending too many API requests.${when} This is not a daily image limit. Bunko will pause API requests until the retry window expires.`,
        icon: Clock,
        variant: 'warning',
      };
    }
    return {
      title: 'Source temporarily rate-limited the proxy',
      body: `The source temporarily blocked the proxy's shared IP address for sending too many requests.${when} This is not your account or a daily image limit. Upgrading your account will not help. Try again later, or point Bunko at a proxy that egresses from a different (e.g. residential) IP.`,
      icon: Clock,
      variant: 'warning',
    };
  }
  if (error instanceof QuotaExceeded) {
    return {
      title: 'Daily image quota reached',
      body: 'You have hit the e-hentai daily image-quota limit. Try again tomorrow, switch to a different gallery, or upgrade your e-hentai account for a higher cap.',
      icon: AlertTriangle,
      variant: 'destructive',
    };
  }
  if (error instanceof ParseError) {
    return {
      title: 'Could not read this page',
      body: 'The source seems to have changed its markup. Try again — if the problem persists, please report it. (Snippet captured for debugging.)',
      icon: AlertTriangle,
      variant: 'warning',
    };
  }
  if (error instanceof NetworkError) {
    return {
      title: 'Network error',
      body:
        error.status !== undefined
          ? `Request failed with status ${error.status}. The proxy or source may be temporarily unavailable.`
          : 'Failed to reach the source through the proxy. Check your connection and the proxy URL in Settings.',
      icon: AlertTriangle,
      variant: 'warning',
    };
  }
  if (error instanceof SourceError) {
    return {
      title: 'Source error',
      body: error.message || 'Unknown source-level error.',
      icon: AlertTriangle,
      variant: 'warning',
    };
  }
  const msg =
    error instanceof Error ? error.message : typeof error === 'string' ? error : 'Unknown error.';
  return {
    title: 'Something went wrong',
    body: msg,
    icon: AlertTriangle,
    variant: 'destructive',
  };
}
